// src/data/news.ts — お知らせの一覧データ（仕様 2026-11-02-news-pages-design §3.1）
// vite.config.ts からも Node で読まれるので、React・画像・CSS を import しない。
// 本文は src/data/articles/ に置き、記事ページだけが動的 import する。

export interface NewsFact {
  label: string;
  value: string; // '\n' で改行
}

export interface NewsLink {
  label: string;
  href: string;
}

export interface NewsItem {
  date: string; // 'YYYY-MM' | 'YYYY-MM-DD'。日まで分かる項目だけ日を書く
  title: string;
  href?: string; // 外部 URL。slug と排他
  slug?: string; // これがある項目が「記事」。URL は `/news/${slug}`
  shortTitle?: string; // パンくず・<title> 用。無ければ title
  summary?: string; // リード文。meta description にも使う。記事では必須
  facts?: readonly NewsFact[]; // 記事ページ左列の事実欄
  related?: readonly NewsLink[]; // 関連リンク（外部）
  ogImage?: string; // 記事専用の OGP 画像（サイト相対パス。scripts/ogp/generate.mjs で生成）。無ければ共通画像
}

export const NEWS_PATH = '/news';

export const NEWS_ITEMS: readonly NewsItem[] = [
  {
    date: '2026-11-02',
    slug: 'aws-leap-regional-supporter',
    title:
      'AWS ジャパン「LEAP by AWS ジャパン」の AWS 地域創生サポーター（石川県）に認定されました',
    shortTitle: 'AWS 地域創生サポーター認定',
    ogImage: '/ogp/news-aws-leap-regional-supporter.png',
    summary:
      '株式会社eNEW Studio（石川県金沢市、代表取締役 伊原健太）は、アマゾン ウェブ サービス ジャパン合同会社が推進する「AWS ジャパン地域創生支援プログラム（LEAP by AWS ジャパン）」において、石川県の「AWS 地域創生サポーター」に認定されました。',
    facts: [
      { label: '認定日', value: '2026 年 11 月 2 日' },
      { label: '認定区分', value: '地域創生サポーター' },
      { label: '登録都道府県', value: '石川県' },
      {
        label: 'プログラム',
        value: 'AWS ジャパン地域創生支援プログラム\n（LEAP by AWS ジャパン）',
      },
    ],
    related: [
      {
        label:
          'AWS ジャパン地域創生支援プログラム（LEAP by AWS ジャパン）を発表 ── AWS 公式ブログ',
        href: 'https://aws.amazon.com/jp/blogs/news/local-economy-acceleration-program/',
      },
      {
        label:
          'AWS パートナーネットワーク（APN）Select Tier Services パートナーに認定されました',
        href: '/news/apn-select-tier-services-partner',
      },
    ],
  },
  {
    date: '2026-10-13',
    slug: 'apn-select-tier-services-partner',
    title:
      'AWS パートナーネットワーク（APN）Select Tier Services パートナーに認定されました',
    shortTitle: 'APN Select Tier 認定',
    ogImage: '/ogp/news-apn-select-tier.png',
    summary:
      '株式会社eNEW Studio（石川県金沢市、代表取締役 伊原健太）は、2026 年 10 月 13 日、AWS パートナーネットワーク（APN）の Select Tier Services パートナーに認定されました。設立から 1 年足らずの少人数の会社ですが、AWS の認定資格と案件実績の要件を満たしたパートナーとして、AWS と連携しながらお客様を支援していきます。',
    facts: [
      { label: '認定日', value: '2026 年 10 月 13 日' },
      { label: '認定区分', value: 'Select Tier Services パートナー' },
      { label: '制度', value: 'AWS パートナーネットワーク（APN）' },
      { label: '拠点', value: '石川県金沢市' },
    ],
    related: [
      {
        label: 'AWS サービスパートナーのティア ── AWS 公式',
        href: 'https://aws.amazon.com/jp/partners/services-tiers/',
      },
    ],
  },
  { date: '2026-05', title: 'コーポレートロゴを刷新しました' },
  {
    date: '2026-04',
    title:
      'TD SYNNEX のブログに「KiroがAWS開発を変える――インフラからアプリまでAIと共創する開発フロー」を寄稿しました',
    href: 'https://jp.tdsynnex.com/blog/cloud/aws-ai-agent-kiro/',
  },
  { date: '2025-12', title: 'コーポレートサイトを公開しました' },
  { date: '2025-11', title: '株式会社eNEW Studio を設立しました' },
];

/** 外部サイトへのリンクか（http/https で始まる） */
export const isExternalHref = (href: string | undefined): boolean =>
  typeof href === 'string' && /^https?:\/\//.test(href);

/** 'YYYY-MM' → 'YYYY.MM'、'YYYY-MM-DD' → 'YYYY.MM.DD'。それ以外はそのまま */
export const formatNewsDate = (date: string): string =>
  date.replaceAll('-', '.');

/** 本文を持つ記事（slug のある項目）。NEWS_ITEMS の順 */
export const getArticles = (): readonly NewsItem[] =>
  NEWS_ITEMS.filter((item) => typeof item.slug === 'string');

export const findArticleBySlug = (slug: string): NewsItem | undefined =>
  NEWS_ITEMS.find((item) => item.slug === slug);

/** 記事なら `/news/${slug}`、外部リンクなら href。どちらも無ければ undefined */
export const getNewsHref = (item: NewsItem): string | undefined => {
  if (item.slug) return `${NEWS_PATH}/${item.slug}`;
  return item.href ? item.href : undefined;
};
